import { useMemo } from 'react';
import dayjs, { type Dayjs } from 'dayjs';
import { DatePicker, Select } from 'antd';
import { PeriodTabs } from '../PeriodTabs';
import { SATIS_DONEM_SECENEKLERI, donemAraligiMetni } from '../satis-analiz/donem';
import { useStokSorgu } from './useStokVerisi';
import { kategoriNormalize } from './kategori';
import { SubeRozeti, subeListesi, type SubeGuvenilirlikSatiri } from './SubeRozeti';
import type { StokKaynaklari } from './ekranKaynaklari';
import type { StokFiltresi } from './useStokFiltre';

const { RangePicker } = DatePicker;

interface Props {
  filtre: StokFiltresi;
  kaynaklar: StokKaynaklari;
}

interface KayipKategoriSatiriKisa { kategori: string; tur: string }

/** Kategori adları 990061'den — yalnız fire satırları (sarf da döner, bu ekranın kategori listesi K2 ile aynı kapsamda kalır). */
function kategoriListesi(rows: KayipKategoriSatiriKisa[]): string[] {
  const kume = new Set<string>();
  for (const r of rows) {
    if (r.tur !== 'fire') continue;
    kume.add(kategoriNormalize(r.kategori));
  }
  return Array.from(kume).sort((a, b) => a.localeCompare(b, 'tr'));
}

/**
 * G4b/C4b — Stok Analizi filtre şeridi: dönem sekmeleri, şube, kategori, özel aralık.
 * `FiltreSeridi.tsx` (Satış Analiz) kalıbı OKUNDU, kopyalanmadı: kontrol boyutları (`size="small"`),
 * "Özel" seçiliyken aralık metninin düşmesi (tekrar önler, tek satırda kalmaya yardımcı olur — kullanıcı
 * 2026-09-16) BİREBİR aynı desen.
 *
 * Şube ve kategori listeleri AYRI bir ds İSTEMEZ: 990050 (şube) ve 990061 (kategori, KAYIP bloğunun
 * "Fire nerede" kartıyla AYNI kaynak/AYNI parametre) zaten mount'ta çekiliyor — `stokQueryKey` aynı
 * olduğu için TanStack Query bu bileşenin sorgusuyla bloklarınkini TEKİLLEŞTİRİR, ek istek gitmez.
 */
export function StokFiltreSeridi({ filtre, kaynaklar }: Props) {
  const f = { donem: filtre.donem, sube: filtre.sube, kategori: filtre.kategori };

  const subeQ = useStokSorgu<SubeGuvenilirlikSatiri>(kaynaklar.subeGuvenilirlik, f);
  const subeler = useMemo(() => subeListesi(subeQ.data?.data ?? []), [subeQ.data]);

  const katQ = useStokSorgu<KayipKategoriSatiriKisa>(kaynaklar.kayipKategori, f);
  const kategoriler = useMemo(() => kategoriListesi(katQ.data?.data ?? []), [katQ.data]);

  const ozelDeger = useMemo<[Dayjs, Dayjs] | null>(
    () => (filtre.ozelAralik ? [dayjs(filtre.ozelAralik.bas), dayjs(filtre.ozelAralik.bit)] : null),
    [filtre.ozelAralik],
  );

  // FiltreSeridi.tsx:37 ile AYNI gerekçe: aralık picker'ın iki input'unda zaten yazıyorken metin TEKRAR.
  const aralikPickerdaYaziyor = filtre.donemTuru === 'ozel' && filtre.ozelAralik !== null;

  return (
    <div data-testid="stok-filtre-seridi" className="min-w-0">
      <div className="flex flex-wrap items-center gap-2 min-h-[28px]" data-testid="stok-donem-secici">
        <PeriodTabs
          value={filtre.donemTuru}
          onChange={(p) => filtre.degistir({ donemTuru: p })}
          secenekler={SATIS_DONEM_SECENEKLERI}
          kompakt
        />

        {/* Şube süzgeci: yalnız BİRDEN FAZLA şube varsa anlamlı (SA'nın `subeler.length > 1` kalıbı). */}
        {subeler.length > 1 && (
          <div className="inline-flex items-center gap-1">
            <div data-testid="stok-sube-secici">
              <Select
                size="small"
                style={{ minWidth: 140 }}
                value={filtre.sube ?? '__tum__'}
                onChange={(v) => filtre.degistir({ sube: v === '__tum__' ? null : v })}
                options={[
                  { value: '__tum__', label: 'Tüm şubeler' },
                  ...subeler.map((s) => ({ value: s, label: s })),
                ]}
              />
            </div>
            {/* §4.5 — şube seçiliyken rozet: 990050'nin AYNI çekilmiş satırlarından, sabit kodlanmaz. */}
            {filtre.sube !== null && (
              <SubeRozeti sube={filtre.sube} satirlar={subeQ.data?.data ?? []} />
            )}
          </div>
        )}

        {kaynaklar.kayipKategori && kategoriler.length > 0 && (
          <div data-testid="stok-kategori-secici">
            <Select
              size="small"
              style={{ minWidth: 140 }}
              value={filtre.kategori ?? '__tum__'}
              onChange={(v) => filtre.degistir({ kategori: v === '__tum__' ? null : v })}
              options={[
                { value: '__tum__', label: 'Tüm kategoriler' },
                ...kategoriler.map((k) => ({ value: k, label: k })),
              ]}
            />
          </div>
        )}

        {!aralikPickerdaYaziyor && (
          <span
            className="inline-flex items-center min-h-[28px] text-[12px] text-ink-500 tnum"
            data-testid="stok-donem-araligi"
          >
            {donemAraligiMetni(filtre.donem)}
          </span>
        )}

        {filtre.donemTuru === 'ozel' && (
          <div data-testid="stok-ozel-aralik">
            <RangePicker
              value={ozelDeger}
              onChange={(v) =>
                filtre.degistir({
                  ozelAralik:
                    v && v[0] && v[1]
                      ? { bas: v[0].format('YYYY-MM-DD'), bit: v[1].format('YYYY-MM-DD') }
                      : null,
                })
              }
              format="DD.MM.YYYY"
              size="small"
              allowClear
            />
          </div>
        )}
      </div>
    </div>
  );
}
